export type WeekPickerOption = {
  id: string;
  weekNumber?: number | null;
  startsAt?: string | Date | null;
  endsAt?: string | Date | null;
};

export type ActiveWeekRef = {
  id?: string | null;
  week?: number | null;
};

const toTime = (value: string | Date | null | undefined): number | null => {
  if (value == null || value === "") {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

/**
 * Week pickers on scores/picks should only list past weeks plus the active week.
 * Future weeks stay hidden so members can't browse unplayed slates.
 *
 * When the active NFL week is in this season, later week numbers are excluded
 * even if their stored dates are missing or stale. Other seasons fall back to
 * start/end dates so historical weeks remain available.
 *
 * Pass a clock from the caller (`getCurrentTime()`) so Storybook and other
 * visual-time overrides are respected. The default `new Date()` is wall-clock.
 */
export function filterPastAndActiveWeeks<T extends WeekPickerOption>(
  weeks: T[],
  activeWeek?: ActiveWeekRef | null,
  now: Date = new Date(),
): T[] {
  if (weeks.length === 0) {
    return weeks;
  }

  const nowTime = now.getTime();
  const activeInList = activeWeek?.id
    ? (weeks.find((week) => week.id === activeWeek.id) ?? null)
    : null;

  return weeks.filter((week) => {
    if (activeInList?.id === week.id) {
      return true;
    }

    if (
      activeInList &&
      typeof week.weekNumber === "number" &&
      typeof activeInList.weekNumber === "number"
    ) {
      return week.weekNumber <= activeInList.weekNumber;
    }

    const startTime = toTime(week.startsAt);
    if (startTime !== null) {
      return startTime <= nowTime;
    }

    const endTime = toTime(week.endsAt);
    if (endTime !== null) {
      return endTime <= nowTime;
    }

    return !activeInList;
  });
}
